import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Clock3,
  Download,
  Eye,
  EyeOff,
  FileUp,
  KeyRound,
  LoaderCircle,
  Play,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import {
  createRequestDraft,
  humanizeApiError,
  KEY_STORAGE,
  makeBrowserBody,
  maskKey,
  normalizeResult,
  requestVariant,
  resolveRequest,
} from '../lib.js';

function headerObject(entries) {
  return Object.fromEntries(entries.map((item) => [item.name, item.value]));
}

function parseEventStream(raw) {
  const events = raw.split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .filter((line) => line && line !== '[DONE]')
    .map((line) => { try { return JSON.parse(line); } catch { return null; } })
    .filter(Boolean);
  let outputText = '';
  let usage = {};
  for (const event of events) {
    const delta = event.choices?.[0]?.delta?.content
      ?? event.delta?.text
      ?? (event.type === 'response.output_text.delta' ? event.delta : '');
    if (typeof delta === 'string') outputText += delta;
    if (event.usage) usage = event.usage;
    if (event.message?.usage) usage = event.message.usage;
  }
  return { object: 'event_stream', output_text: outputText, usage, events };
}

function inferredMediaType(contentType, operation, disposition = '') {
  if (/^(image|audio|video)\//.test(contentType)) return contentType.split('/')[0];
  const hint = `${operation?.path || ''} ${operation?.summary || ''} ${disposition}`.toLowerCase();
  if (/audio|speech|tts|语音|音频/.test(hint)) return 'audio';
  if (/video|视频/.test(hint)) return 'video';
  if (/image|images|图片|图像/.test(hint)) return 'image';
  return 'file';
}

async function readResponse(response, operation) {
  const contentType = response.headers.get('content-type') || '';
  const responseHeaders = Object.fromEntries(response.headers.entries());
  if (/^(image|audio|video)\//.test(contentType) || contentType.includes('application/octet-stream')) {
    const blob = await response.blob();
    return {
      payload: null,
      media: {
        url: URL.createObjectURL(blob),
        type: inferredMediaType(contentType, operation, response.headers.get('content-disposition') || ''),
        contentType,
        size: blob.size,
      },
      responseHeaders,
    };
  }
  const raw = await response.text();
  if (contentType.includes('text/event-stream')) return { payload: parseEventStream(raw), media: null, responseHeaders };
  if (!raw) return { payload: {}, media: null, responseHeaders };
  try { return { payload: JSON.parse(raw), media: null, responseHeaders }; }
  catch { return { payload: { message: raw }, media: null, responseHeaders }; }
}

function RequestField({ parameter, value, onChange }) {
  const id = `parameter-${parameter.in}-${parameter.name}`.replace(/[^a-z0-9_-]/gi, '-');
  return <label className="request-field" htmlFor={id}>
    <span><code>{parameter.name}</code>{parameter.required && <b>必填</b>}<small>{parameter.type}{parameter.in ? ` · ${parameter.in}` : ''}</small></span>
    {parameter.enum?.length ? <select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
      {!parameter.required && <option value="">不发送</option>}
      {parameter.enum.map((item) => <option key={String(item)} value={String(item)}>{String(item)}</option>)}
    </select> : <input id={id} value={value} onChange={(event) => onChange(event.target.value)} placeholder={parameter.required ? '请输入值' : '留空则不发送'} />}
    {parameter.description && <em>{parameter.description}</em>}
  </label>;
}

function FormField({ field, value, file, onValue, onFile }) {
  const binary = field.format === 'binary';
  const fieldHint = `${field.shortName} ${field.description || ''}`.toLowerCase();
  const accept = /image|图像|图片/.test(fieldHint) ? 'image/*' : /audio|音频|语音/.test(fieldHint) ? 'audio/*' : /video|视频/.test(fieldHint) ? 'video/*' : undefined;
  return <label className="request-field form-field">
    <span><code>{field.shortName}</code>{field.required && <b>必填</b>}<small>{binary ? '文件' : field.type}</small></span>
    {binary ? <span className={file ? 'file-picker has-file' : 'file-picker'}>
      <FileUp size={16} />
      <span>{file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : '选择文件'}</span>
      <input type="file" accept={accept} onChange={(event) => onFile(event.target.files?.[0] || null)} />
    </span> : field.enum?.length ? <select value={value} onChange={(event) => onValue(event.target.value)}>
      {!field.required && <option value="">不发送</option>}
      {field.enum.map((item) => <option key={String(item)} value={String(item)}>{String(item)}</option>)}
    </select> : <input value={value} onChange={(event) => onValue(event.target.value)} placeholder={field.required ? '请输入值' : '留空则不发送'} />}
    {field.description && <em>{field.description}</em>}
  </label>;
}

function ResponseView({ result }) {
  if (!result) return null;
  const { payload, media, responseHeaders, status, statusText, latency } = result;
  const normalized = payload ? normalizeResult(payload) : { image: '', audio: '', video: '', text: '', usage: {} };
  const requestId = responseHeaders['x-api-request-id'] || responseHeaders['x-request-id'] || responseHeaders['request-id'] || '';
  const requestCost = normalized.usage?.cost
    ?? normalized.usage?.total_cost
    ?? responseHeaders['x-oneapi-request-cost']
    ?? responseHeaders['x-new-api-request-cost']
    ?? responseHeaders['x-request-cost'];
  const mediaUrl = media?.url || normalized.image || normalized.audio || normalized.video;
  const mediaType = media?.type || (normalized.image ? 'image' : normalized.audio ? 'audio' : normalized.video ? 'video' : '');
  const usefulHeaders = Object.entries(responseHeaders).filter(([name]) => !['content-length', 'content-encoding'].includes(name));
  return <section className="playground-response" aria-live="polite">
    <div className="response-summary">
      <span className={status >= 200 && status < 300 ? 'response-status success' : 'response-status error'}>{status} {statusText}</span>
      <span><Clock3 size={14} /> {(latency / 1000).toFixed(2)}s</span>
      {normalized.usage?.total_tokens !== undefined && <span>Token <b>{normalized.usage.total_tokens}</b>{normalized.usage.input_tokens !== undefined && <small>{normalized.usage.input_tokens} 输入 / {normalized.usage.output_tokens || 0} 输出</small>}</span>}
      {requestCost !== undefined && requestCost !== '' && <span>费用 <b>￥{requestCost}</b></span>}
    </div>
    {requestId && <p className="request-id">请求 ID <code>{requestId}</code></p>}
    {mediaUrl && mediaType === 'image' && <a className="media-result" href={mediaUrl} target="_blank" rel="noreferrer"><img src={mediaUrl} alt="接口返回图片" /></a>}
    {mediaUrl && mediaType === 'audio' && <audio className="media-player" src={mediaUrl} controls />}
    {mediaUrl && mediaType === 'video' && <video className="media-player" src={mediaUrl} controls playsInline />}
    {media?.url && <a className="download-result" href={media.url} download><Download size={15} /> 下载返回文件</a>}
    {normalized.text && <p className="response-text">{normalized.text}</p>}
    {payload && <details className="response-details" open>
      <summary>完整响应 <ChevronDown size={15} /></summary>
      <pre><code>{JSON.stringify(payload, null, 2)}</code></pre>
    </details>}
    {usefulHeaders.length > 0 && <details className="response-details">
      <summary>响应头 <span>{usefulHeaders.length}</span><ChevronDown size={15} /></summary>
      <dl>{usefulHeaders.map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}</dl>
    </details>}
  </section>;
}

export function Playground({ open, onClose, operation, draft, setDraft, apiKey, setApiKey, initialModel = '' }) {
  const [keyDraft, setKeyDraft] = useState(apiKey);
  const [showKey, setShowKey] = useState(false);
  const [keyMessage, setKeyMessage] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const resultMediaRef = useRef('');
  const abortRef = useRef(null);
  const resolved = useMemo(() => resolveRequest(operation, draft, apiKey), [operation, draft, apiKey]);
  const variant = requestVariant(operation, draft.contentType);
  const pathParameters = (operation.parameters || []).filter((item) => item.in === 'path');
  const queryParameters = (operation.parameters || []).filter((item) => item.in === 'query' && !(operation.auth && ['key', 'api_key', 'apikey'].includes(item.name.toLowerCase())));
  const headerParameters = (operation.parameters || []).filter((item) => item.in === 'header' && !['authorization', 'x-api-key', 'content-type'].includes(item.name.toLowerCase()));
  const formFields = (variant?.fields || []).filter((field) => Number(field.depth || 0) === 0);
  const multipart = draft.contentType.includes('multipart/form-data');

  useEffect(() => {
    setKeyDraft(apiKey);
  }, [apiKey]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('dialog-open');
    const close = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', close);
    return () => {
      abortRef.current?.abort();
      document.body.classList.remove('dialog-open');
      document.removeEventListener('keydown', close);
    };
  }, [open, onClose]);

  useEffect(() => () => {
    if (resultMediaRef.current) URL.revokeObjectURL(resultMediaRef.current);
  }, []);

  useEffect(() => {
    setError('');
    setResult(null);
    setProgress('');
  }, [operation.id]);

  if (!open) return null;

  const setParameter = (parameter, value) => setDraft((current) => ({
    ...current,
    parameters: { ...current.parameters, [`${parameter.in}:${parameter.name}`]: value },
  }));

  const changeContentType = (contentType) => setDraft(createRequestDraft(operation, contentType, initialModel));

  const saveKey = async () => {
    const value = keyDraft.trim();
    if (!value) { setKeyMessage('请输入 API Key'); return; }
    setKeyMessage('正在验证…');
    try {
      const response = await fetch('/api/playground/v1/models', { headers: { Authorization: `Bearer ${value}`, Accept: 'application/json' } });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const info = humanizeApiError(payload.error?.message || payload.message || `HTTP ${response.status}`, response.status);
        throw new Error(info.message);
      }
      localStorage.setItem(KEY_STORAGE, value);
      setApiKey(value);
      setKeyMessage(`连接成功 · ${payload.data?.length || 0} 个模型`);
    } catch (reason) {
      setKeyMessage(reason.message || '验证失败');
    }
  };

  const clearKey = () => {
    localStorage.removeItem(KEY_STORAGE);
    setApiKey('');
    setKeyDraft('');
    setKeyMessage('已从当前浏览器清除');
  };

  const publishResult = (parsed, response, started) => {
    if (resultMediaRef.current) URL.revokeObjectURL(resultMediaRef.current);
    resultMediaRef.current = parsed.media?.url || '';
    const finalResult = { ...parsed, status: response.status, statusText: response.statusText, latency: performance.now() - started };
    setResult(finalResult);
    return finalResult;
  };

  const pollMidjourney = async (taskId, started, signal) => {
    for (let attempt = 1; attempt <= 80; attempt += 1) {
      setProgress(`任务已提交，正在查询结果 · ${attempt}/80`);
      await new Promise((resolve) => window.setTimeout(resolve, attempt < 5 ? 1200 : 2000));
      const response = await fetch(`/api/playground/mj/task/${encodeURIComponent(taskId)}/fetch`, {
        headers: { Authorization: `Bearer ${apiKey}`, Accept: 'application/json' },
        signal,
      });
      const parsed = await readResponse(response, { ...operation, path: '/mj/task/{id}/fetch' });
      publishResult(parsed, response, started);
      if (!response.ok) {
        const info = humanizeApiError(parsed.payload?.error?.message || parsed.payload?.description || parsed.payload?.message || `HTTP ${response.status}`, response.status);
        throw new Error([info.message, info.detail].filter(Boolean).join(' · '));
      }
      const state = String(parsed.payload?.status || parsed.payload?.state || '').toUpperCase();
      const completed = ['SUCCESS', 'SUCCEEDED', 'COMPLETED', 'DONE'].includes(state) || Number(parsed.payload?.progress || 0) >= 100;
      const failed = ['FAILURE', 'FAILED', 'ERROR', 'CANCELLED'].includes(state);
      if (completed) { setProgress('任务已完成'); return; }
      if (failed) throw new Error(parsed.payload?.failReason || parsed.payload?.message || 'Midjourney 任务失败');
    }
    throw new Error('任务仍在运行，可稍后使用任务查询接口继续查看。');
  };

  const run = async () => {
    if (operation.auth && !apiKey) { setKeyMessage('请先保存并验证 API Key'); return; }
    if (resolved.bodyError) { setError(`JSON 格式错误：${resolved.bodyError}`); return; }
    const missingFormField = multipart && formFields.find((field) => field.required && (field.format === 'binary' ? !draft.files[field.shortName] : !draft.formValues[field.shortName]));
    if (missingFormField) { setError(`请填写必填字段：${missingFormField.shortName}`); return; }
    if (operation.method === 'DELETE' && !window.confirm('该请求可能删除数据，确定继续发送吗？')) return;
    setRunning(true);
    setProgress('');
    setError('');
    setResult(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const started = performance.now();
    try {
      const response = await fetch(`/api/playground${resolved.relativeUrl}`, {
        method: resolved.method,
        headers: headerObject(resolved.headers),
        body: makeBrowserBody(resolved),
        signal: controller.signal,
      });
      const parsed = await readResponse(response, operation);
      publishResult(parsed, response, started);
      if (!response.ok) {
        const info = humanizeApiError(parsed.payload?.error?.message || parsed.payload?.description || parsed.payload?.message || `HTTP ${response.status}`, response.status);
        throw new Error([info.message, info.detail].filter(Boolean).join(' · '));
      }
      if (operation.method === 'POST' && /^\/mj\/submit\/(?!upload-discord-images$)/.test(operation.path)) {
        const taskId = parsed.payload?.result || parsed.payload?.taskId || parsed.payload?.id;
        if (taskId) await pollMidjourney(String(taskId), started, controller.signal);
      }
    } catch (reason) {
      if (reason.name !== 'AbortError') setError(reason.message || '请求失败');
    } finally {
      setRunning(false);
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  return <div className="playground-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <aside className="playground-drawer" role="dialog" aria-modal="true" aria-label={`调用 ${operation.summary}`}>
      <header className="playground-head">
        <div><span>API WORKBENCH</span><h2>现场调用</h2><p>{operation.method} {resolved.relativeUrl}</p></div>
        <button onClick={onClose} aria-label="关闭调试器"><X size={20} /></button>
      </header>

      <div className="playground-body">
        {operation.auth && <section className="playground-section credential-section">
          <div className="playground-section-title"><KeyRound size={17} /><div><h3>{apiKey ? `已连接 ${maskKey(apiKey)}` : '连接 API Key'}</h3><p>只保存在当前浏览器，不会写入文档服务器。</p></div></div>
          <div className="key-control-slot">{apiKey ? <div className="saved-key-control"><span><Check size={15} />已保存到浏览器</span><button onClick={clearKey}><Trash2 size={14} />清除</button></div> : <div className="key-entry">
            <label><input type={showKey ? 'text' : 'password'} value={keyDraft} onChange={(event) => setKeyDraft(event.target.value)} placeholder="sk-..." autoComplete="off" /><button type="button" onClick={() => setShowKey((value) => !value)} aria-label={showKey ? '隐藏 Key' : '显示 Key'}>{showKey ? <EyeOff size={16} /> : <Eye size={16} />}</button></label>
            <button onClick={saveKey}>验证并保存</button>
          </div>}</div>
          <p className={keyMessage ? 'key-message' : 'key-message is-empty'} role="status">{keyMessage || '\u00a0'}</p>
        </section>}

        <section className="playground-section request-config">
          <div className="request-preview"><span>{resolved.method}</span><code>{resolved.relativeUrl}</code><button onClick={() => setDraft(createRequestDraft(operation, '', initialModel))} title="恢复示例"><RotateCcw size={15} /></button></div>
          {operation.request?.contentTypes?.length > 1 && <label className="content-type-select"><span>请求格式</span><select value={draft.contentType} onChange={(event) => changeContentType(event.target.value)}>{operation.request.contentTypes.map((item) => <option key={item}>{item}</option>)}</select></label>}

          {pathParameters.length > 0 && <div className="request-fields"><h3>路径参数</h3>{pathParameters.map((parameter) => <RequestField key={`${parameter.in}-${parameter.name}`} parameter={parameter} value={draft.parameters[`path:${parameter.name}`] || ''} onChange={(value) => setParameter(parameter, value)} />)}</div>}
          {queryParameters.length > 0 && <div className="request-fields"><h3>查询参数</h3>{queryParameters.map((parameter) => <RequestField key={`${parameter.in}-${parameter.name}`} parameter={parameter} value={draft.parameters[`query:${parameter.name}`] || ''} onChange={(value) => setParameter(parameter, value)} />)}</div>}
          {headerParameters.length > 0 && <div className="request-fields"><h3>请求头</h3>{headerParameters.map((parameter) => <RequestField key={`${parameter.in}-${parameter.name}`} parameter={parameter} value={draft.parameters[`header:${parameter.name}`] || ''} onChange={(value) => setParameter(parameter, value)} />)}</div>}

          {operation.request && !multipart && <label className="json-editor"><span>请求 JSON</span><textarea value={draft.bodyText} onChange={(event) => setDraft((current) => ({ ...current, bodyText: event.target.value }))} spellCheck="false" /></label>}
          {operation.request && multipart && <div className="request-fields multipart-fields"><h3>表单与文件</h3>{formFields.map((field) => <FormField key={field.shortName} field={field} value={draft.formValues[field.shortName] || ''} file={draft.files[field.shortName] || null} onValue={(value) => setDraft((current) => ({ ...current, formValues: { ...current.formValues, [field.shortName]: value } }))} onFile={(file) => setDraft((current) => ({ ...current, files: { ...current.files, [field.shortName]: file } }))} />)}</div>}
        </section>

        <button className="send-request" disabled={running} onClick={run}>{running ? <LoaderCircle className="spin" size={17} /> : <Play size={17} />}{running ? (progress || '正在请求…') : '发送请求'}</button>
        {error && <div className="playground-error"><AlertCircle size={17} /><span>{error}</span></div>}
        <ResponseView result={result} />
      </div>
    </aside>
  </div>;
}
